const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

type TokenListener = (token: string | null) => void;

const tokenListeners = new Set<TokenListener>();

const notifyTokenChange = (token: string | null) => {
  tokenListeners.forEach((listener) => listener(token));
};

export const onTokenChange = (listener: TokenListener) => {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
};

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = (accessToken: string, refreshToken?: string | null) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  notifyTokenChange(accessToken);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  notifyTokenChange(null);
};

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return atob(normalized + padding);
};

interface JwtPayload {
  exp?: number;
  iat?: number;
  tokenType?: string;
  [key: string]: unknown;
}

export const parseJwt = (token: string): JwtPayload | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    return JSON.parse(decodeBase64Url(payload));
  } catch (error) {
    return null;
  }
};

export const getTokenExpiry = (token: string): number | null => {
  const payload = parseJwt(token);
  return typeof payload?.exp === 'number' ? payload.exp : null;
};

export const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  const exp = getTokenExpiry(token);
  if (!exp) {
    return false;
  }
  return Date.now() >= exp * 1000 - bufferSeconds * 1000;
};

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://172.235.245.13/api'
  : '/api';

let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (!data?.token) {
        return null;
      }

      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      const nextRefreshToken = data.refreshToken || refreshToken;
      setTokens(data.token, nextRefreshToken);
      return data.token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const getValidAccessToken = async (): Promise<string | null> => {
  const token = getAccessToken();
  if (token && !isTokenExpired(token)) {
    return token;
  }

  if (!getRefreshToken()) {
    return null;
  }

  return refreshAccessToken();
};
